import React, { useEffect, useState } from 'react'
import { fmt } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { CompanyPicker, costGet, useCompany, useMeta } from './CostCommon.jsx'

// 원가관리 · 구매원가명세서.
// 제조원가명세서가 '만든 것'을 다루듯 이건 '사 온 것'을 다룬다. 월 총평균단가
// = (이월금액 + 당월 매입액) / (이월수량 + 당월 매입수량). 출고·기말은 그 단가로 평가한다.

export default function CostPurchase({ month, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [company, setCompany] = useCompany()
  const [meta] = useMeta()
  const [data, setData] = useState(null)
  const [type, setType] = useState('전체')
  const [filter, setFilter] = useState('')
  const [err, setErr] = useState('')

  useEffect(() => {
    setErr(''); setData(null)
    costGet('/purchase-statement', { month, company }).then(setData).catch((e) => setErr(e.message))
  }, [month, company])

  if (err) return <div className="error-box">{err}</div>
  if (!data) return <div className="muted">불러오는 중…</div>

  const q = filter.trim().toLowerCase()
  const rows = data.rows.filter((r) =>
    (type === '전체' || r.item_type === type) &&
    (!q || [r.sku, r.name, r.option_name, r.category].some((v) => (v || '').toLowerCase().includes(q))))
  const sum = (k) => rows.reduce((s, r) => s + (r[k] || 0), 0)

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>{month} 구매원가명세서</h2>
        <NoteDrawer target="purchase" screenKey="cost_purchase"
          context={{ month }} contextLabel={month}
          onNavigate={(ctx) => ctx?.month && onMonthChange?.(ctx.month)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <CompanyPicker value={company} onChange={setCompany} meta={meta} />
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {['전체', '상품', '원재료', '부재료', '저장품'].map((v) => <option key={v}>{v}</option>)}
        </select>
        <input placeholder="검색 (품번/상품명/구분)" style={{ width: 260 }}
          value={filter} onChange={(e) => setFilter(e.target.value)} />
        <span className="muted">{rows.length}품목</span>
      </div>
      <div className="hint">
        총평균단가 = (이월금액 + 당월 매입액) ÷ (이월수량 + 당월 매입수량).
        매입단가는 그 달에 실제로 산 값이고, 총평균단가가 출고·기말 평가에 쓰이는 값입니다.
        둘이 크게 벌어지면 이월 재고가 지금 시세와 다르다는 뜻입니다.
      </div>

      <div className="cards">
        <div className="card"><div className="label">당월 매입</div><div className="value">{fmt(sum('buyQty'))}개</div></div>
        <div className="card"><div className="label">당월 매입액</div><div className="value">{fmt(sum('buyAmt'))}원</div></div>
        <div className="card"><div className="label">평균 매입단가</div><div className="value">{sum('buyQty') ? fmt(sum('buyAmt') / sum('buyQty'), 1) : '-'}원</div></div>
        <div className="card"><div className="label">기말 재고금액</div><div className="value">{fmt(sum('closingAmt'))}원</div></div>
      </div>

      <div className="panel">
        <table>
          <thead>
            <tr>
              {!company && <th rowSpan={2} className="l">사업부</th>}
              <th rowSpan={2} className="l">품번</th>
              <th rowSpan={2} className="l">상품명</th>
              <th rowSpan={2}>구분</th>
              <th colSpan={2}>이월</th>
              <th colSpan={3}>당월 매입</th>
              <th rowSpan={2}>총평균단가</th>
              <th colSpan={2}>판매출고</th>
              <th colSpan={2}>타계정출고</th>
              <th colSpan={2}>기말</th>
            </tr>
            <tr>
              <th>수량</th><th>금액</th>
              <th>수량</th><th>매입단가</th><th>금액</th>
              <th>수량</th><th>금액</th>
              <th>수량</th><th>금액</th>
              <th>수량</th><th>금액</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={16} className="c muted">해당 월 구매·재고 움직임이 없습니다</td></tr>}
            {rows.slice(0, 800).map((r) => (
              <tr key={r.item_id}>
                {!company && <td className="l">{r.company_name}</td>}
                <td className="l">{r.sku}</td>
                <td className="l">{r.name} {r.option_name && <span className="muted">{r.option_name}</span>}</td>
                <td className="c">{r.item_type}</td>
                <td>{fmt(r.openQty)}</td><td>{fmt(r.openAmt)}</td>
                <td>{fmt(r.buyQty)}</td>
                <td>{r.buyQty ? fmt(r.buyUnit, 1) : '-'}</td>
                <td>{fmt(r.buyAmt)}</td>
                <td><b>{fmt(r.avgCost, 1)}</b></td>
                <td>{fmt(r.salesQty)}</td><td>{fmt(r.salesAmt)}</td>
                <td>{fmt(r.otherQty)}</td><td>{fmt(r.otherAmt)}</td>
                <td className={r.closingQty < 0 ? 'warn' : ''}>{fmt(r.closingQty)}</td>
                <td>{fmt(r.closingAmt)}</td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="total">
                <td className="l" colSpan={company ? 3 : 4}>합계</td>
                <td>{fmt(sum('openQty'))}</td><td>{fmt(sum('openAmt'))}</td>
                <td>{fmt(sum('buyQty'))}</td><td />
                <td>{fmt(sum('buyAmt'))}</td>
                <td />
                <td>{fmt(sum('salesQty'))}</td><td>{fmt(sum('salesAmt'))}</td>
                <td>{fmt(sum('otherQty'))}</td><td>{fmt(sum('otherAmt'))}</td>
                <td>{fmt(sum('closingQty'))}</td><td>{fmt(sum('closingAmt'))}</td>
              </tr>
            )}
          </tbody>
        </table>
        {rows.length > 800 && <div className="hint">상위 800건만 표시 중 — 검색으로 좁혀주세요.</div>}
      </div>
    </>
  )
}
